/**
 * Casino business-day helpers for player ACTIVITY timestamps.
 *
 * Two traps these exist to avoid:
 *
 * 1. player_activities.open_date (and TableCaptain's Open_Date / lastPlayed /
 *    firstSession / lastSession) store PACIFIC WALL TIME in a UTC-typed column —
 *    TableCaptain writes local time and the sync tags it 'Z'. Verified from the
 *    session histogram: zero sessions 03:00-08:59 with an evening peak, which is
 *    only true if the stored number is already Pacific. So NEVER let `new Date()`
 *    parse these as UTC; that shifts them 7-8 hours and renders a 1am session as
 *    the previous day.
 *
 * 2. A gaming day runs 10:00 -> 06:00 the next morning. Play before 6 AM belongs
 *    to the PRIOR business day, so a 10pm-2am session is ONE visit on ONE day.
 *    Rollover hour matches the backend's casinoTime.js, recurring-reward resets,
 *    and the 10:00-06:00 business-hours report window.
 *
 * NOTE: users.createdAt IS genuine UTC — use a normal Date for that, not this.
 */
const ROLLOVER_HOUR = 6;
const CASINO_TZ = 'America/Los_Angeles';
const MS_PER_DAY = 86400000;

const shiftYmd = (ymd, days) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d) + days * MS_PER_DAY).toISOString().slice(0, 10);
};

/** Business date ("YYYY-MM-DD") for an activity timestamp. Read literally, never parsed. */
export function activityBusinessDate(stamp) {
  if (!stamp) return null;
  const s = String(stamp);
  const ymd = s.slice(0, 10);
  const hour = parseInt(s.slice(11, 13), 10);
  if (Number.isNaN(hour)) return ymd;
  return hour < ROLLOVER_HOUR ? shiftYmd(ymd, -1) : ymd;
}

/** Today's business date ("YYYY-MM-DD") in casino-local terms. */
export function currentBusinessDate(ref = new Date()) {
  const ymd = ref.toLocaleDateString('en-CA', { timeZone: CASINO_TZ });
  const hour = parseInt(ref.toLocaleString('en-US', {
    timeZone: CASINO_TZ, hour: '2-digit', hour12: false,
  }), 10);
  return hour < ROLLOVER_HOUR ? shiftYmd(ymd, -1) : ymd;
}

/** Whole business days between an activity and now. 0 = tonight's gaming day. */
export function businessDaysSince(stamp, ref = new Date()) {
  const from = activityBusinessDate(stamp);
  if (!from) return null;
  const to = currentBusinessDate(ref);
  return Math.max(0, Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY
  ));
}

/** "Today" / "Yesterday" / "3d ago" / "2mo ago" / "Never", in business days. */
export function businessDayLabel(stamp) {
  if (!stamp) return 'Never';
  const d = businessDaysSince(stamp);
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 30) return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

/** Locale date string for the activity's BUSINESS date. No timezone conversion. */
export function formatBusinessDate(stamp, opts = { month: 'short', day: 'numeric', year: 'numeric' }) {
  const ymd = activityBusinessDate(stamp);
  if (!ymd) return 'N/A';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', opts);
}

/** The stored wall-clock time, verbatim, for tooltips. */
export function casinoWallTime(stamp) {
  if (!stamp) return '';
  const s = String(stamp);
  return `${s.slice(0, 10)} ${s.slice(11, 16)} (casino time)`;
}
